import { type FunctionComponent } from 'react';
import { DEFAULT_PAGESIZE, PAGESIZE_OPTIONS, SEARCH_RESULT_LIMIT } from '../../../constants/search';
import { ClickableElement } from '../../buttons';
import ChevronLeftIcon from '../../svgs/chevron-left.svg';
import ChevronRightIcon from '../../svgs/chevron-right.svg';
import { SelectBox, type SelectOptionProps, type SelectSize } from '../select-box/select-box.component';

export type PaginationProps = {
	page: number;
	setPage: (nextPage: number) => void;
	totalCount: number;
	pageSize?: number;
	setPageSize?: (pageSize: number) => void;
	hidePageSizeSelector?: boolean;
	size?: SelectSize;
	// a method to call after the user has interacted with the component
	onChange?: () => void;
	// Analytics region for activity map tracking.
	analyticsRegion?: string;
	hidePageNumbers?: boolean;
};

const generatePageOptions = (numPages: number, hidePageNumbers?: boolean): SelectOptionProps[] => {
	const options: SelectOptionProps[] = [];
	for (let page = 1; page <= numPages; page++) {
		options.push({ name: hidePageNumbers ? '' : `${page} of ${numPages}`, value: String(page) });
	}
	return options;
};

const generatePageSizeOptions = (): SelectOptionProps[] => {
	return PAGESIZE_OPTIONS.map((size) => ({ name: `Results per page: ${size}`, value: `${size}` }));
};

const SIZE_MAP = {
	SMALL: 'input-small f6 ph2',
	DEFAULT: 'input f5 pv2 ph3',
	LARGE: 'input-large f4 ph3'
};

export const Pagination: FunctionComponent<PaginationProps> = ({
	page,
	pageSize = DEFAULT_PAGESIZE,
	setPage,
	setPageSize,
	totalCount,
	size = 'DEFAULT',
	hidePageSizeSelector = false,
	onChange,
	hidePageNumbers = false
}) => {
	let numPages = Math.ceil(totalCount / pageSize);
	if (numPages * pageSize > SEARCH_RESULT_LIMIT) {
		numPages = Math.floor(SEARCH_RESULT_LIMIT / pageSize);
	}
	const currentPage = page > numPages ? numPages + 1 : page;
	const pageOptions = generatePageOptions(numPages, hidePageNumbers);
	const pageSizeOptions = generatePageSizeOptions();
	const sizeClass = SIZE_MAP[size];
	const baseClass = 'flex items-center ba br2 theme-grey-darker';
	const hoverClass = 'hover-theme-primary hover-b--theme-primary';
	const disabledClass = 'not-allowed b--theme-grey-light theme-grey-light';
	const backDisabled = currentPage <= 1;
	const forwardDisabled = currentPage >= numPages;

	const callOnChange = () => {
		if (onChange) {
			onChange();
		}
	};

	const pageBack = () => {
		if (backDisabled) {
			return;
		}
		setPage(currentPage - 1);
		callOnChange();
	};

	const pageForward = () => {
		if (forwardDisabled) {
			return;
		}
		setPage(currentPage + 1);
		callOnChange();
	};

	const handlePageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		event.preventDefault();
		const value = Number(event.target.value);
		setPage(value);
		callOnChange();
	};

	const handleResultsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		event.preventDefault();
		const value = Number(event.target.value);
		if (setPageSize) {
			setPageSize(value);
			callOnChange();
		}
	};
	const resultCountFloor = (page - 1) * pageSize + 1;
	const resultCountCeiling = pageSize * page;
	const resultCountCeilingDisplay = resultCountCeiling > totalCount ? totalCount : resultCountCeiling;
	const totalCountDisplay = totalCount > SEARCH_RESULT_LIMIT ? `${SEARCH_RESULT_LIMIT.toLocaleString()}+` : totalCount.toLocaleString();

	return (
		<div className="flex flex-column flex-row-ns items-center justify-center w-100-ns justify-between-ns">
			<div className={`${sizeClass} lh-title w-33-ns`}>
				Showing{' '}
				<b>
					{resultCountFloor.toLocaleString()} - {resultCountCeilingDisplay.toLocaleString()}
				</b>{' '}
				of <b>{totalCountDisplay}</b> results
			</div>
			<div className="flex justify-between mt3 mt0-ns ml-auto-ns mr-auto-ns">
				<ClickableElement
					ariaLabel="page back"
					className={`${baseClass} ${sizeClass} ${backDisabled ? disabledClass : hoverClass} ${hidePageNumbers ? 'mr2' : ''}`}
					onClick={pageBack}
					automationHook="previous-page"
					testId="page-back">
					<ChevronLeftIcon className={`${backDisabled ? disabledClass : ''}`} />
				</ClickableElement>
				{!hidePageNumbers && (
					<div className={'mh2'}>
						<SelectBox
							options={pageOptions}
							hideArrow={true}
							selectSize={size}
							onChange={handlePageChange}
							value={currentPage}
							ariaLabel="page number"
							borderStyle="dark"
							disabled={hidePageNumbers}
						/>
					</div>
				)}
				<ClickableElement
					ariaLabel="page forward"
					className={`${baseClass} ${sizeClass} ${forwardDisabled ? disabledClass : hoverClass}`}
					onClick={pageForward}
					automationHook="next-page"
					testId="page-forward">
					<ChevronRightIcon className={`${forwardDisabled ? disabledClass : ''}`} />
				</ClickableElement>
			</div>
			<div className="mt3 mt0-ns w-33-ns tr-ns">
				{!hidePageSizeSelector && setPageSize && (
					<SelectBox
						automationHook="results-per-page"
						options={pageSizeOptions}
						selectSize={size}
						onChange={handleResultsPerPageChange}
						value={pageSize}
						ariaLabel="results per page"
						borderStyle="dark"
					/>
				)}
			</div>
		</div>
	);
};
